import { ReactNode, useState } from "react";
import { SocialIcon } from "../utils/social-icon";
import { ItemWithMetaData, RelevantEntity } from "../api/Items";
import { XIcon } from "../shared-components/icons";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  useRemoveClientAddedProfile,
  useRemoveProfile,
} from "../api/ProfilesApi";
import { classNames } from "../utils";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import { formatProfileAddedByLabel } from "./util";

import ReactTooltip from "react-tooltip";

type ProfileRowProps = {
  item: ItemWithMetaData;
  tabColor?: string;
  onDelete?: (itemUUID: string) => void;
  className?: string;
  children?: ReactNode;
  isManualAdd?: boolean;
  isReadOnly?: boolean;
  isSidebar?: boolean;
  showAddedBy?: boolean;
};

function ProfileRow({
  item,
  tabColor,
  onDelete,
  className,
  children,
  isManualAdd,
  isReadOnly,
  isSidebar,
  showAddedBy,
}: ProfileRowProps) {
  const [selectedEntity, setSelectedEntity] = useState<{
    entityField: string;
    entities: { score: string; entities: string }[];
  }>();
  const profileUrl = item.item.content.url || "";
  const addedByLabel = formatProfileAddedByLabel(item.item.content?.added_by);

  const { canDeleteClientAddedProfile, canInternalQaEdit, canClientQaEdit } =
    usePermissionValidator();
  const itemIsLowPermission =
    item.item.content?.added_by === "INVESTIGATOR" && !item.item.is_reviewed;
  const canDeleteProtectedProfile =
    canDeleteClientAddedProfile || canInternalQaEdit || canClientQaEdit;
  const canDeleteThisProfile = itemIsLowPermission || canDeleteProtectedProfile;
  const { modal, hide, show } = useConfirmationDialog();

  const {
    isLoading: isLoadingRemoveClientAddedProfile,
    mutate: removeClientAddedProfile,
    isSuccess: successRemoveClientAddedProfile,
  } = useRemoveClientAddedProfile();
  const {
    isLoading: isLoadingRemoveProfile,
    mutate: removeProfile,
    isSuccess: successRemoveProfile,
  } = useRemoveProfile();

  const handleRemoveProfileImpl = () => {
    if (canDeleteProtectedProfile) {
      // this endpoint can now handle deleting any profile
      // so choice is only based on permissions
      removeClientAddedProfile(item.item.external_id);
    } else {
      removeProfile(item.item.external_id);
    }
  };

  function handleRemoveProfile() {
    show(
      "Remove Profile?",
      `Removing this profile will also remove all posts associated with this profile. Do you still wish to continue?`,
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          handleRemoveProfileImpl();
          hide();
        }
      }
    );
  }

  if (successRemoveClientAddedProfile || successRemoveProfile) {
    return null;
  }
  return (
    <>
      <div className="bg-white my-3">
        <div
          className={classNames(
            `${className} rounded-lg py-2.5 px-2 border border-gray-300`,
            isSidebar ? "break-all whitespace-normal" : ""
          )}
        >
          <div
            className={classNames(
              `flex flex-1 items-center`,
              isSidebar ? "" : "overflow-hidden"
            )}
          >
            <div
              className={classNames(
                `flex flex-1 items-center`,
                isSidebar ? "" : "overflow-hidden"
              )}
            >
              <div className="w-19">
                <SocialIcon url={profileUrl} />
              </div>
              <a
                href={profileUrl}
                target="_blank"
                rel="noreferrer"
                className={classNames(
                  `ml-3 text-base font-normal leading-6`,
                  isSidebar ? "" : "truncate"
                )}
                data-tip={profileUrl}
              >
                {profileUrl}
              </a>
              <ReactTooltip
                className="hover:visible:opacity-1 w-96"
                delayHide={1000}
                effect="solid"
              />
              {canDeleteThisProfile && !isSidebar && (
                <div className="px-5 ml-auto">
                  {(isLoadingRemoveProfile ||
                    isLoadingRemoveClientAddedProfile) && <LoadingSpinner />}
                  {!(
                    isLoadingRemoveProfile || isLoadingRemoveClientAddedProfile
                  ) && (
                    <>
                      {showAddedBy && addedByLabel && (
                        <span className="pt-0.5 pb-0.5 pr-2.5 pl-2 rounded-xl bg-sky-100">
                          Added by:&nbsp;
                          <span className="font-semibold">{addedByLabel}</span>
                        </span>
                      )}
                      <button onClick={handleRemoveProfile} className="pl-2">
                        <XIcon />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="pl-20">
            {!isManualAdd && (
              <MatchingEntitiesConfirm
                item={item}
                setSelected={setSelectedEntity}
              />
            )}
          </div>

          {selectedEntity?.entityField && (
            <ReactTooltip
              id={selectedEntity.entityField}
              place="top"
              type="light"
              effect="solid"
              backgroundColor="white"
            >
              <div>
                {selectedEntity.entities?.map((e, i) => (
                  <div key={i}>{`${e.score} of ${e.entities}`}</div>
                ))}
              </div>
            </ReactTooltip>
          )}
        </div>
      </div>
      {modal}
    </>
  );
}

type MatchingEntitiesProps = { item: ItemWithMetaData; setSelected?: any };

export const MatchingEntitiesConfirm = ({
  item,
  setSelected,
}: MatchingEntitiesProps) => {
  const matchingEntities: RelevantEntity[] = item.entities;
  const entitiesMap: Map<string, RelevantEntity[]> = new Map();
  matchingEntities.forEach((entity: RelevantEntity) => {
    let current = entitiesMap.get(entity.field) || [];
    current.concat(entity);
    entitiesMap.set(entity.field, current);
  });
  if (entitiesMap.size === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-3 pt-1">
      {entitiesMap &&
        Array.from(entitiesMap.keys()).map((entityField: string) => {
          const elements = matchingEntities
            .filter((entity: RelevantEntity) => entity.field === entityField)
            .map((entity: RelevantEntity) => ({
              score: JSON.parse(entity.value).score || 0,
              entities: JSON.parse(entity.value).entities || [],
            }));
          if (elements.reduce((a, b) => a + b.score, 0) === 0) {
            return null;
          }
          const valueFields = elements
            .map((e) => e.entities.map((f: any) => f[0]))
            .flat();

          return (
            <div
              key={entityField}
              data-tooltip
              data-for={entityField}
              onMouseEnter={() => {
                setSelected?.({ entityField, entities: elements });
              }}
              onMouseLeave={() => {
                // setSelected({});
              }}
              className="flex flex-row border border-gray-300 rounded-lg p-2"
            >
              <div className={"font-medium text-xs mr-2"}>
                <span className="font-bold capitalize">{entityField}</span>
                {valueFields.map((v) => (
                  <div>{v}</div>
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default ProfileRow;
